// 설레임런 DTO.
// 참고: store/server/common/doosanbears/doosanbears.dto.ts
// 엔드포인트 경로는 BE 스펙 그대로 `/seoleim/...` 사용 (모듈 식별자는 seoleimrun 통일).

// 메인(이벤트 기본 정보) 조회 response.
// ⚠️ BE 스펙 미확정 placeholder. 실제 응답 스키마로 교체할 것.
export interface GetSeoleimrunMainResponse {
  title: string;
  start_dttm: number;
  end_dttm: number;
}

// =============================================================================
// [마이페이지] 당첨·영수증·선물 건수 조회
// GET /seoleim/my-page/info
// =============================================================================
export interface GetSeoleimrunMyPageInfoResponse {
  /** 선물 건수 */
  gift: number;
  /** 영수증 건수 */
  receipt: number;
  /** 경품 당첨 건수 */
  luckydraw: number;
}

// =============================================================================
// [마이페이지] 선물하기 내역 조회
// GET /seoleim/my-page/gift
// =============================================================================
export interface SeoleimrunGiftItem {
  product_name: string;
  phone: string;
  status: string;
  /** unix timestamp(초) */
  reg_dttm: number;
}

export interface GetSeoleimrunGiftListResponse {
  list: SeoleimrunGiftItem[];
}

// =============================================================================
// [마이페이지] 영수증 인증 내역
// GET /seoleim/my-page/receipt
// =============================================================================
export interface SeoleimrunReceiptItem {
  /** 승인번호 */
  approve_number: string;
  /** 영수증 원본 이미지 */
  image: string;
  /** 인증 날짜(unix timestamp, 초) */
  reg_dttm: number;
}

export interface GetSeoleimrunReceiptListResponse {
  list: SeoleimrunReceiptItem[];
}

// =============================================================================
// [마이페이지] 경품 당첨 내역
// GET /seoleim/my-page/luckydraw
// =============================================================================
export interface SeoleimrunLuckydrawItem {
  idx: number;
  /** 경품명 */
  name: string;
  /** 정보 작성 여부 */
  write_yn: 'Y' | 'N';
  /** 당첨 일자(unix timestamp, 초) */
  reg_dttm: number;
}

export interface GetSeoleimrunLuckydrawListResponse {
  list: SeoleimrunLuckydrawItem[];
}

// =============================================================================
// [경품] 당첨자 정보 작성
// POST /seoleim/luckydraw/user-info
// =============================================================================
export interface CreateSeoleimrunLuckydrawUserInfoRequest {
  idx: number;
  name?: string;
  phone: string;
}

export interface CreateSeoleimrunLuckydrawUserInfoResponse {
  result: string;
}

// =============================================================================
// [경품] 당첨자 정보 조회
// GET /seoleim/luckydraw/user-info
// =============================================================================
export interface GetSeoleimrunLuckydrawUserInfoRequest {
  idx: number;
}

export interface GetSeoleimrunLuckydrawUserInfoResponse {
  name?: string;
  phone: string;
}

// =============================================================================
// [경품] 당첨내역 상세조회
// GET /seoleim/luckydraw/detail
// =============================================================================
export type SeoleimrunLuckydrawDetailType = 'order' | 'receipt';

export interface GetSeoleimrunLuckydrawDetailRequest {
  type: SeoleimrunLuckydrawDetailType;
  /** order_id 또는 receipt_idx 중 하나를 string 으로 전달 */
  value: string;
}

export interface SeoleimrunLuckydrawDetailItem {
  /** 당첨 상품명 */
  product_name: string;
}

export interface GetSeoleimrunLuckydrawDetailResponse {
  /** 당첨 상품 목록 — 꽝만 있는 경우 빈 배열. 당첨 갯수는 배열 길이로 판단 */
  list: SeoleimrunLuckydrawDetailItem[];
}

// =============================================================================
// [영수증] 인증하기
// POST /seoleim/receipt
// =============================================================================
export interface CreateSeoleimrunReceiptRequest {
  /** 영수증 이미지 base64 data URL (예: 'data:image/jpeg;base64,...'). presigned 업로드 없이 본문으로 직접 전송. */
  image: string;
}

export interface CreateSeoleimrunReceiptResponse {
  /** 인증된 영수증 식별자 — 경품뽑기 응모(/seoleimrun/luckydraw/receipt/[receiptIdx]) 진입에 사용 */
  receipt_idx: number;
}

/** 영수증 인증 실패 상태 코드 (응답 envelope status). getAxiosErrorStatus() 로 추출 */
export const SEOLEIMRUN_RECEIPT_ERROR_STATUS = {
  NOT_RECEIPT: '3001', // 영수증이 아님
  OCR_BLACKLISTED: '3003', // OCR 블랙리스트 사용자
  ALREADY_REGISTERED: '3004', // 이미 등록된 영수증
  NOT_PURCHASED: '3005', // 설레임 미구매
  INVALID_GEMINI_FORMAT: '3010', // Gemini 응답 포맷 오류
} as const;

export type SeoleimrunReceiptErrorStatus =
  (typeof SEOLEIMRUN_RECEIPT_ERROR_STATUS)[keyof typeof SEOLEIMRUN_RECEIPT_ERROR_STATUS];

// =============================================================================
// [참가] 참가자 정보 조회 / 입력·수정
// GET /seoleim/apply  ·  POST /seoleim/apply
// ⚠️ BE 엔드포인트 경로 미확정("/경로") — placeholder. 확정 시 교체할 것.
// =============================================================================
export interface SeoleimrunApplyOption {
  product_idx: number;
  /** 티셔츠: 'S'|'M'|'L'|'XL' / 선글라스: 'black'|'orange' */
  product_name: string;
  isSelected: 'Y' | 'N';
  /** 잔여수량 */
  remain: number;
}

export interface GetSeoleimrunApplyResponse {
  verified_yn: 'Y' | 'N';
  name: string;
  /** YYYY-MM-DD */
  birthday: string;
  gender: 'male' | 'female';
  /** - 없이 */
  phone: string;
  /** HH:MM:SS */
  expected10kmTime: string;
  /** 비상 연락처(- 없이) */
  emergencyPhone: string;
  tshirtOptions: SeoleimrunApplyOption[];
  sunglassesOptions: SeoleimrunApplyOption[];
  /** 결제 완료된 주문만 존재 — 있으면 읽기전용/수정 불가 */
  order_id?: string;
}

export interface UpsertSeoleimrunApplyRequest {
  tshirtIdx: number;
  sunglassesIdx: number;
  /** HH:MM:SS */
  expected10kmTime: string;
  /** - 없이 */
  emergencyPhone: string;
}

export interface UpsertSeoleimrunApplyResponse {
  result: string;
}
